import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * Only created for a dialog that opts in via `dialog-non-modal-breakpoint`
   * (see the getter below) — every other `<dialog-component>` on the site
   * (quick-add, zoom, popups, ...) keeps today's showModal()-only behaviour,
   * completely untouched.
   * @type {MediaQueryList | null}
   */
  #modalQuery = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    if (this.nonModalBreakpoint > 0) {
      this.#modalQuery = window.matchMedia(`(max-width: ${this.nonModalBreakpoint - 1}px)`);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
    removeTrapFocus();
  }

  /**
   * True once this dialog is opted into `nonModalBreakpoint` AND the
   * viewport is at/above it — i.e. it will open with `.show()` instead of
   * `.showModal()`, so it never enters the browser's top layer (a
   * compositing layer above the entire document that no z-index, however
   * large, can be drawn above — see luxury-interactive-engine's custom
   * cursor, the reason this exists). Below the breakpoint (or when not
   * opted in at all) this is always false and every behaviour below falls
   * straight through to the original showModal() path.
   *
   * Trade-off (mirrors <theme-drawer>'s own desktop, non-modal mode for the
   * cart, which accepts the exact same one): `trapFocus()` keeps Tab
   * cycling inside the dialog, but — unlike a real showModal() — the rest of
   * the page is not made `inert`, so assistive tech could still reach
   * content behind the dimmed backdrop that a sighted user can't see.
   *
   * @returns {boolean}
   */
  get #isNonModal() {
    return this.#modalQuery !== null && !this.#modalQuery.matches;
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      if (this.#isNonModal) {
        dialog.show();
        trapFocus(dialog);
        this.#focusInitialElement();
      } else {
        dialog.showModal();
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * showModal() auto-focuses an [autofocus] descendant, falling back to the
   * dialog itself when there isn't one. .show() does neither on its own, so
   * the non-modal path replicates that same default manually.
   */
  #focusInitialElement() {
    const { dialog } = this.refs;
    const autofocusEl = dialog.querySelector('[autofocus]');
    if (autofocusEl instanceof HTMLElement) {
      autofocusEl.focus();
    } else {
      dialog.focus();
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    // No-op (and safe to call unconditionally) when this dialog never used
    // trapFocus() in the first place — see removeTrapFocus()'s own guard.
    removeTrapFocus();
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }

  /**
   * Viewport width (px) below which this dialog still uses the browser's
   * native showModal() (full native modal semantics: top layer, focus trap,
   * inert background, Esc-to-close). At or above it, `showDialog()` opens it
   * with `.show()` instead (see `#isNonModal`). 0 (the default, unset) means
   * "always use showModal()" — i.e. unchanged behaviour unless a consumer
   * explicitly sets this attribute.
   *
   * @returns {number}
   */
  get nonModalBreakpoint() {
    return Number(this.getAttribute('dialog-non-modal-breakpoint')) || 0;
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
